import { useEffect, useState, VFC } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { MasterDataStore, useMasterData } from 'src/api/masterData'
import { asModal } from 'src/components/hoc/asModal'
import { CATEGORY_DICT } from 'src/data/masterData'
import { MasterData, MasterDataWithCategory } from 'src/models/MasterData'
import {
  _formalert,
  _menuicon,
  _message_submenu_subtitle,
  black,
  orange,
  safetyOrange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold, fontWeightMedium } from 'src/styles/font'
import styled from 'styled-components'
import {
  Control,
  DeselectButton,
  FormItemProps,
  SelectableItemDiv,
  SelectableItems,
} from './common'
import { FormSelectedItems } from './FormSelectedItems'

type FormMultiSelectProps = FormItemProps & { items: MasterData[] }

export const MasterDataMultiSelect: VFC<FormMultiSelectProps> = ({
  id,
  items,
  label,
}) => {
  const { register, watch, reset } = useFormContext()
  const selectedValues = (watch(id) as MasterData[]) || []
  const updateSelectedValue = (selected: MasterData, checked: boolean) => {
    const currentValues = selectedValues || []
    if (checked) reset({ [id]: [...currentValues, selected] })
    else
      reset({
        [id]: currentValues.filter((each) => each.value !== selected.value),
      })
  }
  const selectAll = () => reset({ [id]: items })
  const deselectAll = () => reset({ [id]: [] })
  useEffect(() => {
    register(id)
  }, [id, register])
  return (
    <Layout>
      <Title>{label}</Title>
      <Control>
        <button type="button" onClick={selectAll}>
          すべて選択
        </button>
        <DeselectButton type="button" onClick={deselectAll}>
          解除
        </DeselectButton>
      </Control>
      <Selection
        items={items}
        updateSelectedValue={updateSelectedValue}
        currentValues={selectedValues}
      />
    </Layout>
  )
}

const Selection: VFC<{
  items: MasterData[]
  currentValues: MasterData[] | MasterData | undefined
  updateSelectedValue: (selected: MasterData, checked: boolean) => void
}> = ({ items, currentValues, updateSelectedValue }) => {
  // 配列でない場合は配列に変換
  const safeCurrentValues = Array.isArray(currentValues)
    ? currentValues
    : currentValues && 'value' in currentValues
    ? [currentValues]
    : []
  return (
    <SelectableContainer>
      <SelectableItems>
        {items.map((option) => (
          <SelectableItemDiv key={option.value}>
            <input
              id={option.value}
              type="checkbox"
              onChange={({ target: { checked } }) =>
                updateSelectedValue(option, checked)
              }
              checked={safeCurrentValues.some(
                ({ value }) => option.value === value,
              )}
            />
            <label htmlFor={option.value}>{option.label}</label>
          </SelectableItemDiv>
        ))}
      </SelectableItems>
    </SelectableContainer>
  )
}

const Layout = styled.div`
  margin-top: 4px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.2);
  padding: 16px 24px;
`
const Title = styled.h2`
  margin-bottom: 24px;
  color: ${_message_submenu_subtitle};
  font-size: 18px;
  font-weight: ${fontWeightMedium};
`

const SelectableContainer = styled.div`
  max-height: 40vh;
  overflow-y: auto;
`

// MasterDataStoreからtypeを受け取ってマスターデータを自動取得する複数選択コンポーネント（モーダル形式）
export const MasterDataMultiSelectWithType: VFC<
  Omit<FormMultiSelectProps, 'items'> & { type: keyof MasterDataStore }
> = ({ type, ...props }) => {
  const { data } = useMasterData()
  // businessTypesはMasterDataWithCategory[]、それ以外はMasterData[]の可能性がある
  const items = data
    ? (data[type] as (MasterData | MasterDataWithCategory)[])
    : []
  const [openModal, setOpenModal] = useState(false)
  const { getValues, watch, setValue } = useFormContext()
  const currentValue = watch(props.id)

  // 初期値が単一のMasterDataの場合、配列に変換
  // ProfileFormのreset後に値が設定されるため、currentValueの変更を監視
  useEffect(() => {
    const value = getValues(props.id)
    // 値が存在し、配列でない場合（単一のMasterData）は配列に変換
    if (value && !Array.isArray(value) && 'value' in value)
      setValue(props.id, [value], { shouldDirty: false })
    // 値が未設定の場合は何もしない（空配列にしない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]) // currentValueが変更されたときに実行

  const selectedItems = getValues(props.id) as (
    | MasterData
    | MasterDataWithCategory
  )[]
  const selectedLabels = Array.isArray(selectedItems)
    ? selectedItems
        .filter((item) => item != null)
        .map((item: MasterData | MasterDataWithCategory) => item.label)
    : []

  return (
    <>
      <FormSelectedItems
        id={props.id}
        label={props.label}
        items={selectedLabels}
        setIsOpen={() => setOpenModal(true)}
        optional={props.optional}
      />
      {openModal && (
        <BusinessTypeMultiSelectModal
          id={props.id}
          label={props.label}
          close={() => setOpenModal(false)}
          items={items}
        />
      )}
    </>
  )
}

// モーダルコンポーネント（カテゴリー対応）
const BusinessTypeMultiSelectModal = asModal<{
  id: string
  label: string
  items: (MasterData | MasterDataWithCategory)[]
  close: () => void
}>(
  ({ id, label, items, close }) => {
    const { setValue, getValues } = useFormContext()
    const { handleSubmit } = useForm()
    const currentValue = getValues(id)
    // 初期値が単一のMasterDataの場合、配列に変換
    const initialValue = Array.isArray(currentValue)
      ? currentValue
      : currentValue && 'value' in currentValue
      ? [currentValue]
      : []
    const [selectedItems, setSelectedItems] =
      useState<(MasterData | MasterDataWithCategory)[]>(initialValue)
    const [errorMessages] = useState('')

    // カテゴリ情報を抽出（MasterDataWithCategoryの場合）
    const itemsWithCategory = items.filter(
      (item): item is MasterDataWithCategory => 'category' in item,
    )
    const categories = Array.from(
      new Set(itemsWithCategory.map((item) => item.category)),
    ).filter((v) => v)
    const [selectedCategory, setSelectedCategory] = useState(
      categories.length > 0 ? categories[0] : undefined,
    )

    // 選択されたカテゴリでフィルタリング
    const filteredItems = selectedCategory
      ? itemsWithCategory.filter((item) => item.category === selectedCategory)
      : itemsWithCategory

    // 各カテゴリの選択数を計算
    const getSelectedCountByCategory = (category: string) => {
      const selectedItemsWithCategory = selectedItems.filter(
        (item): item is MasterDataWithCategory =>
          'category' in item && item.category === category,
      )
      return selectedItemsWithCategory.length
    }

    const handleOnSubmit = () => {
      setValue(id, selectedItems)
      close()
    }

    const updateItems = (
      selected: MasterData | MasterDataWithCategory,
      checked: boolean,
    ) => {
      if (checked) setSelectedItems([...selectedItems, selected])
      else
        setSelectedItems(
          selectedItems.filter((each) => each.value !== selected.value),
        )
    }

    const getBorderRadiusCss = (index: number, length: number) => {
      if (index === 0) return '16px 0 0 0 ;'
      else if (index + 1 === length) return '0 0 0 16px;'
      else return ''
    }

    const selectAll = () => {
      const array = [...selectedItems, ...filteredItems]
      // 重複削除
      const filteredArray = array.filter(
        (arrayItem, index, self) =>
          self.findIndex((selfItem) => selfItem.value === arrayItem.value) ===
          index,
      )
      setSelectedItems(filteredArray)
    }

    const deselectAll = () => {
      // selectedItemsからfilteredItemsを削除
      const array = selectedItems.filter(
        (item) => !filteredItems.map((item) => item.value).includes(item.value),
      )
      setSelectedItems(array)
    }

    // カテゴリがない場合は従来の表示
    if (categories.length === 0)
      return (
        <ModalLayout>
          <TitleDiv>
            <h1>{`${label}登録`}</h1>
          </TitleDiv>
          <SelectionDiv>
            <SpecialtyDiv>
              <h1>{label}を選択</h1>
              <ModalControl>
                <SelectAllButton
                  type="button"
                  onClick={() => setSelectedItems(items)}
                >
                  すべて選択
                </SelectAllButton>
                |
                <DeselectAllButton
                  type="button"
                  onClick={() => setSelectedItems([])}
                >
                  解除
                </DeselectAllButton>
              </ModalControl>
              <ModalButtonsDiv>
                {items?.map((item) => {
                  const checked = selectedItems
                    .map((item) => item.value)
                    .some((value) => item.value === value)

                  return (
                    <span key={`${item.value}_${item.label}`}>
                      <input
                        id={item.value}
                        type="checkbox"
                        onChange={({ target: { checked } }) =>
                          updateItems(item, checked)
                        }
                        checked={checked}
                      />
                      <label
                        htmlFor={item.value}
                        style={{
                          background: checked ? orange : truewhite,
                          color: checked ? truewhite : black,
                          border: checked
                            ? `1px solid ${safetyOrange}`
                            : `1px solid ${_menuicon}`,
                        }}
                      >
                        {item.label}
                      </label>
                    </span>
                  )
                })}
              </ModalButtonsDiv>
            </SpecialtyDiv>
          </SelectionDiv>
          <SaveButtonDiv>
            <SaveButton
              type={'button'}
              onClick={handleOnSubmit}
              disabled={!!errorMessages}
            >
              保存する
            </SaveButton>
            <ErrorMessages>
              <p>{errorMessages}</p>
            </ErrorMessages>
          </SaveButtonDiv>
        </ModalLayout>
      )

    // カテゴリがある場合はカテゴリ別表示
    return (
      <ModalLayout>
        <TitleDiv>
          <h1>{`${label}登録`}</h1>
        </TitleDiv>
        <SelectionDiv>
          <FlexDiv>
            <FieldTypeDiv>
              {categories.map((category, index, array) => {
                const selectedCount = getSelectedCountByCategory(category)
                return (
                  <FieldTypeButton
                    key={index}
                    type={'button'}
                    onClick={() => setSelectedCategory(category)}
                    selected={category === selectedCategory}
                    borderRadius={getBorderRadiusCss(index, array.length)}
                    height={100 / array.length}
                  >
                    <CategoryLabel>
                      {CATEGORY_DICT[category] || category}
                    </CategoryLabel>
                    {selectedCount > 0 && (
                      <SelectedCount>{selectedCount}</SelectedCount>
                    )}
                  </FieldTypeButton>
                )
              })}
            </FieldTypeDiv>
            <SpecialtyDiv withCategory={true}>
              <h1>
                {selectedCategory
                  ? `${
                      CATEGORY_DICT[selectedCategory] || selectedCategory
                    }を選択`
                  : `${label}を選択`}
              </h1>
              <ModalControl>
                <SelectAllButton type="button" onClick={selectAll}>
                  すべて選択
                </SelectAllButton>
                |
                <DeselectAllButton type="button" onClick={deselectAll}>
                  解除
                </DeselectAllButton>
              </ModalControl>
              <ModalButtonsDiv>
                {filteredItems?.map((item) => {
                  const checked = selectedItems
                    .map((item) => item.value)
                    .some((value) => item.value === value)

                  return (
                    <span key={`${item.value}_${item.label}`}>
                      <input
                        id={item.value}
                        type="checkbox"
                        onChange={({ target: { checked } }) =>
                          updateItems(item, checked)
                        }
                        checked={checked}
                      />
                      <label
                        htmlFor={item.value}
                        style={{
                          background: checked ? orange : truewhite,
                          color: checked ? truewhite : black,
                          border: checked
                            ? `1px solid ${safetyOrange}`
                            : `1px solid ${_menuicon}`,
                        }}
                      >
                        {item.label}
                      </label>
                    </span>
                  )
                })}
              </ModalButtonsDiv>
            </SpecialtyDiv>
          </FlexDiv>
        </SelectionDiv>
        <SaveButtonDiv>
          <SaveButton
            type={'button'}
            onClick={handleSubmit(handleOnSubmit)}
            disabled={!!errorMessages}
          >
            保存する
          </SaveButton>
          <ErrorMessages>
            <p>{errorMessages}</p>
          </ErrorMessages>
        </SaveButtonDiv>
      </ModalLayout>
    )
  },
  { scrollable: true },
)

const ModalLayout = styled.div`
  border-radius: 16px;
  width: 928px;
  max-width: min(80vw, 928px);
  text-align: center;
  white-space: pre-wrap;
  line-height: 1.4;

  @media screen and (max-width: 960px) {
    max-width: min(90vw, 700px);
  }
`

const TitleDiv = styled.div`
  display: flex;
  margin: 30px auto;
  padding: 22.5px 40px;
  border-radius: 16px;
  background-color: ${truewhite};
  text-align: left;

  h1 {
    font-size: 24px;
    font-weight: ${fontWeightBold};
    line-height: 20px;
    white-space: nowrap;
  }

  @media screen and (max-width: 960px) {
    padding: 15px 20px;
    margin-bottom: 20px;

    h1 {
      font-size: 20px;
      line-height: 20px;
    }
  }

  @media screen and (max-width: 520px) {
    padding: 10px 15px;
    margin-bottom: 20px;

    h1 {
      font-size: 16px;
      line-height: 16px;
    }
  }
`

const SelectionDiv = styled.div`
  background-color: ${truewhite};
  border-radius: 16px;
  text-align: center;
  white-space: pre-wrap;
  line-height: 1.4;
`

const FlexDiv = styled.div`
  display: flex;
  min-height: 250px;
`

const FieldTypeDiv = styled.div`
  max-width: 260px;
`

const FieldTypeButton = styled.button<{
  selected: boolean
  borderRadius: string
  height: number
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: ${(props) => `${props.height}%`};
  margin: 0 auto;
  padding: 15px 10px;
  font-weight: ${fontWeightBold};
  background-color: ${(props) => (props.selected ? `${orange}` : 'white')};
  color: ${(props) => (props.selected ? 'white' : 'black')};
  border-radius: ${(props) => props.borderRadius};
  border: 1px solid ${_menuicon};
  box-shadow: 1px 1px 5px 0.2px #999;
  white-space: nowrap;

  @media screen and (max-width: 960px) {
    font-size: 12px;
    padding: 10px;
  }

  @media screen and (max-width: 520px) {
    font-size: 10px;
    padding: 8px;
  }
`

const CategoryLabel = styled.span`
  display: block;
`

const SelectedCount = styled.span`
  display: inline-block;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: ${fontWeightBold};
  line-height: 24px;
  text-align: center;
  background-color: ${safetyOrange};
  color: ${truewhite};
  border-radius: 12px;

  @media screen and (max-width: 960px) {
    min-width: 20px;
    height: 20px;
    font-size: 10px;
    line-height: 20px;
    padding: 0 5px;
  }

  @media screen and (max-width: 520px) {
    min-width: 18px;
    height: 18px;
    font-size: 9px;
    line-height: 18px;
    padding: 0 4px;
  }
`

const SpecialtyDiv = styled.div<{ withCategory?: boolean }>`
  max-width: ${(props) => (props.withCategory ? '728px' : '928px')};
  padding: 16px 24px;
  text-align: left;

  h1 {
    font-size: 22px;
    color: ${_menuicon};
  }

  @media screen and (max-width: 960px) {
    max-width: ${(props) => (props.withCategory ? '600px' : '800px')};
    padding: 12px 16px;

    h1 {
      font-size: 18px;
    }
  }

  @media screen and (max-width: 520px) {
    padding: 8px 12px;

    h1 {
      font-size: 16px;
    }
  }
`

const ModalControl = styled.div`
  display: flex;
  margin-top: 16px;
  margin-bottom: 24px;
  color: ${_menuicon};

  @media screen and (max-width: 960px) {
    button {
      font-size: 12px;
    }
  }
`

const SelectAllButton = styled.button`
  margin-right: 12px;
  color: ${black};
  font-size: 14px;
  font-weight: ${fontWeightBold};
`

const DeselectAllButton = styled.button`
  margin-left: 12px;
  color: ${safetyOrange};
  font-size: 14px;
  font-weight: ${fontWeightBold};
`

const ModalButtonsDiv = styled.div`
  margin-top: 20px;

  input {
    display: none;
  }

  label {
    display: inline-block;
    margin-right: 10px;
    margin-bottom: 10px;
    padding-left: 20px;
    padding-right: 20px;
    width: auto;
    font-size: 12px;
    font-weight: ${fontWeightBold};
    text-align: center;
    line-height: 30px;
    cursor: pointer;
    border: 1px solid ${_menuicon};
    border-radius: 15px;
    white-space: nowrap;
  }

  @media screen and (max-width: 960px) {
    label {
      font-size: 10px;
      margin-right: 8px;
      margin-bottom: 8px;
      padding-left: 8px;
      padding-right: 8px;
      line-height: 26px;
    }
  }

  @media screen and (max-width: 520px) {
    label {
      font-size: 8px;
      margin-right: 6px;
      margin-bottom: 6px;
      padding-left: 6px;
      padding-right: 6px;
      line-height: 24px;
    }
  }
`

const SaveButtonDiv = styled.div`
  display: flex;
  margin: 30px auto;
  padding: 20px 40px;
  line-height: 1.4;
  border-radius: 16px;
  background-color: ${truewhite};
  white-space: pre-wrap;
  text-align: left;

  @media screen and (max-width: 960px) {
    padding: 15px 20px;
    margin-top: 20px;
    font-size: 12px;
  }

  @media screen and (max-width: 520px) {
    padding: 10px 15px;
  }
`

const SaveButton = styled.button`
  color: ${orange};
  background-color: #ff6c0033;
  padding: 10px 40px;
  border: 1px solid ${orange};
  border-radius: 5px;
`

const ErrorMessages = styled.div`
  color: ${_formalert};
  text-align: center;
  padding: 10px 40px;
`
